import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { Toaster } from '@/components/core/sonner';
import { TooltipProvider } from '@/components/core/tooltip';
import { COMMIT_SHA1, DATADOG_APP_ID, DATADOG_CLIENT_TOKEN, DEPLOYMENT_STAGE } from '@/env';
import { ThemeProvider } from '@/shared/providers/ThemeProvider';

import { GatewayProvider } from './gateways/GatewayProvider';
import { LocalStorageFeatureFlagGateway } from './gateways/impl/LocalStorageFeatureFlagGateway';
import { Home } from './pages/home';
import { useDatadogUser } from './shared/hooks/useDatadogUser';
import { getLogger } from './shared/logger/Logger';
import { Auth0Provider } from './shared/providers/Auth0Provider';
import { FeatureFlagConfigProvider } from './shared/providers/FeatureFlagConfigProvider';
import { FeatureFlagProvider } from './shared/providers/FeatureFlagProvider';
import { MessagesProvider } from './shared/providers/MessagesProvider';
import './styles/ant/index.less';
import './styles/index.css';

// TODO ENG-3377 - remove this once Ant is removed

const logger = getLogger();

if (DATADOG_CLIENT_TOKEN && DATADOG_APP_ID && DEPLOYMENT_STAGE && DEPLOYMENT_STAGE !== 'LOCAL') {
  const version = `${COMMIT_SHA1}-${DEPLOYMENT_STAGE.toLowerCase()}`;

  datadogRum.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    applicationId: DATADOG_APP_ID,
    site: 'us3.datadoghq.com',
    version,
    service: 'lynk-frontend',
    env: DEPLOYMENT_STAGE,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 1,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    silentMultipleInit: true,
    allowedTracingUrls: [/https:\/\/app\..*lynksupplychain\.com/],
  });

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'us3.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: 'lynk-frontend',
    env: DEPLOYMENT_STAGE,
    version,
    forwardConsoleLogs: ['warn', 'error'],
    forwardReports: 'all',
  });
  logger.info(`Datadog RUM and Logs initialized for version ${version}`);
} else {
  logger.warn('Datadog RUM not initialized');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const localStorageFeatureFlagGateway = new LocalStorageFeatureFlagGateway({
  storage: window.localStorage,
});

function DatadogUserSync() {
  useDatadogUser();

  return null;
}

function App() {
  // careful with provider order - some depend on others
  // Auth0Provider depends on Router
  // GatewayProvider depends on Auth0Provider
  return (
    <FeatureFlagConfigProvider featureFlagGateway={localStorageFeatureFlagGateway}>
      <FeatureFlagProvider featureFlagGateway={localStorageFeatureFlagGateway}>
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <BrowserRouter>
              <Auth0Provider>
                <DatadogUserSync />
                <ThemeProvider>
                  <TooltipProvider delayDuration={500}>
                    <MessagesProvider>
                      <GatewayProvider>
                        <Home />
                        <Toaster />
                      </GatewayProvider>
                    </MessagesProvider>
                  </TooltipProvider>
                </ThemeProvider>
              </Auth0Provider>
            </BrowserRouter>
          </NuqsAdapter>
        </QueryClientProvider>
      </FeatureFlagProvider>
    </FeatureFlagConfigProvider>
  );
}

const root = createRoot(document.getElementById('root') as HTMLDivElement);
root.render(<App />);
